import { computed, inject, Injectable, signal } from '@angular/core';

import { Store } from '@ngrx/store';
import moment from 'moment-timezone';

import { AppNavigatorService } from '@core/navigation-legacy';
import { RecentNotesActions, RecentNotesSelectors } from '@core/notes-data';
import { Note, RecentNotesFilters } from '@core/notes-types';

const PAGINATION_LIMIT = 30;

@Injectable({ providedIn: 'root' })
export class RecentNotesService {
  private readonly store = inject(Store);
  private readonly appNavigator = inject(AppNavigatorService);

  public activeTab = signal(RecentNotesFilters.All);

  public recentNotes = computed(() =>
    this.store.selectSignal(
      RecentNotesSelectors.entitiesWithReferences({ scope: this.activeTab() }),
    )(),
  );
  public isPending = computed(() =>
    this.store.selectSignal(RecentNotesSelectors.isPending({ scope: this.activeTab() }))(),
  );

  public changeTab(tab: RecentNotesFilters) {
    this.activeTab.set(tab);
    this.fetchRecentNotes(1);
  }

  public fetchRecentNotes(page = 1) {
    this.store.dispatch(
      RecentNotesActions.fetchRecentNotes({
        payload: {
          scope: this.activeTab(),
          params: {
            limit: PAGINATION_LIMIT,
            page: page,
          },
        },
      }),
    );
  }

  public groupNotes(
    notes: Note[],
  ): Array<{ key: string; notes: Array<{ key: string; notes: Note[] }> }> {
    const groupedByDate = this.groupNotesBy(
      notes,
      note => note.createdAt,
      date => moment(date).format('D MMMM YYYY'),
    );

    return groupedByDate.map(group => ({
      key: group.key,
      notes: this.groupNotesBy(
        group.notes.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1)),
        note => note.candidate?.name!,
      ),
    }));
  }

  private groupNotesBy<T>(
    notes: Note[],
    keyExtractor: (note: Note) => T,
    valueExtractor?: (value: T) => T,
  ): Array<{ key: T; notes: Note[] }> {
    return notes.reduce<Array<{ key: T; notes: Note[] }>>((groups, note) => {
      const key = valueExtractor ? valueExtractor(keyExtractor(note)) : keyExtractor(note);
      const group = groups.find(gp => gp.key === key);

      if (group) {
        group.notes.push(note);
      } else {
        groups.push({ key, notes: [note] });
      }

      return groups;
    }, []);
  }
}
